import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  addAgencyCoupon,
  addCardDetails,
  deleteCardDetail,
  getAgencyInvoiceTransactions,
  fetchAgencyInvoice,
  getBillingHistories,
  getBillingHistory,
  getCardDetails,
  payAgencyDues,
  removeAgencyCoupon,
  updateCardDetail,
  cancelAgencySubscription
} from './api';
import { queryClient } from '@/providers/AllProviders';
import { InvoiceTransactionResponse } from './types';
import type { CancelSubscriptionPayload } from '@/components/Common/CancelSubscription/types';

export function useGetAgencyBillingHistories(params?: Record<string, any>, options?: UseQueryOptions<any, Error>) {
  return useQuery<any>({
    queryKey: ['agency_billing_histories'],
    queryFn: () => getBillingHistories(params),
    ...options
  });
}

export function useGetAgencyBillingHistory(options?: UseQueryOptions<any, Error>) {
  return useQuery<any>({
    queryKey: ['agency_billing_history'],
    queryFn: () => getBillingHistory(),
    ...options
  });
}

export function useGetAgencyCancellationPreview(options?: UseQueryOptions<any, Error>) {
  return useQuery<any>({
    queryKey: ['agency_billing_history_cancellation_preview'],
    queryFn: () => getBillingHistory({ cancellation_preview: true }),
    ...options
  });
}

export function useGetCardDetails(options?: any) {
  return useQuery<any>({
    queryKey: ['agency_card_details'],
    queryFn: () => getCardDetails(),
    ...options
  });
}

export function useUpdateCardDetails() {
  return useMutation({
    mutationFn: (id: string) => updateCardDetail(id),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['agency_card_details']
      });
    }
  });
}

export function useDeleteCard() {
  return useMutation({
    mutationFn: (id: string) => deleteCardDetail(id),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['agency_card_details']
      });
    }
  });
}
export function useAddCardDetails() {
  return useMutation({
    mutationFn: () => addCardDetails({ redirect_uri: window.location.href }),
    onSuccess: (data: { checkout_url: string }) => {
      if (data.checkout_url) window.open(data.checkout_url, '_blank');
    }
  });
}

export function useAddAgencyCoupon() {
  return useMutation({
    mutationFn: (data: { business_id: number; code: string }) => addAgencyCoupon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agency_billing_history'],
        refetchType: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['agency_billing_histories']
      });
    }
  });
}

export function useRemoveAgencyCoupon() {
  return useMutation({
    mutationFn: (data: { business_id: number; code: string }) => removeAgencyCoupon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agency_billing_history'],
        refetchType: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['agency_billing_histories']
      });
    }
  });
}

export function usePayAgencyDues() {
  return useMutation({
    mutationFn: () => payAgencyDues(),
    onSuccess: () => {}
  });
}

export function useFetchAgencyInvoice() {
  return useMutation({
    mutationFn: (id: number) => fetchAgencyInvoice(id),
    onSuccess: (data: { url: string }) => {
      if (data.url) window.open(data.url, '_blank');
    }
  });
}

export function useGetAgencyInvoiceTransactions(
  invoiceId?: number,
  isBusinessView?: boolean,
  options?: UseQueryOptions<InvoiceTransactionResponse[], Error>
) {
  return useQuery<InvoiceTransactionResponse[]>({
    queryKey: ['agency_invoice_transactions', invoiceId],
    queryFn: () => getAgencyInvoiceTransactions(invoiceId!),
    enabled: Boolean(invoiceId && !isBusinessView),
    ...options
  });
}

export function useCancelAgencySubscription() {
  return useMutation({
    mutationFn: (payload?: CancelSubscriptionPayload) => cancelAgencySubscription(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency_billing_history'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['agency_billing_histories'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['agency_card_details'], refetchType: 'active' });
    }
  });
}
